import { create } from "zustand";

import type { DocgenResponse, DocumentSection } from "@/api/schemas";

type Bucket = "izjave" | "naslovnice" | "tekstualni_dio" | "tables";

/**
 * Sprint B — W6. Keyboard shortcuts dispatch ``PendingAction`` records
 * the focused ``DocSectionCard`` consumes once and clears. We use an
 * action queue (rather than a synchronous callback) because the
 * shortcut handler lives at the ``DocgenView`` level but the actual
 * UI state (sheet open, expanded) is owned per-card.
 */
export type PendingAction =
  | { sectionId: string; type: "toggle" }
  | { sectionId: string; type: "regenerate" }
  | { sectionId: string; type: "refine" };

interface DocgenStore {
  data: DocgenResponse | null;
  setData: (data: DocgenResponse | null) => void;
  /** Replace one section in-place (keeps array order). */
  patchSection: (sectionId: string, next: Partial<DocumentSection>) => void;
  /** Locate a section across buckets. */
  findSection: (sectionId: string) =>
    | { bucket: Bucket; index: number; section: DocumentSection }
    | null;

  // ---- Sprint B — W6 keyboard shortcuts -----------------------------------
  /** Section currently targeted by ``j``/``k`` navigation. */
  focusedSectionId: string | null;
  setFocusedSection: (id: string | null) => void;
  /** One-shot action published by the shortcut handler, consumed by the card. */
  pendingAction: PendingAction | null;
  requestAction: (action: PendingAction) => void;
  clearPendingAction: (sectionId: string) => void;
}

const BUCKETS: Bucket[] = ["izjave", "naslovnice", "tekstualni_dio", "tables"];

/**
 * In-memory store for the live docgen response. Pages subscribe via
 * the hook; persistence (localStorage) is intentionally _not_ here —
 * see ``useProjectStore`` (A-W5).
 */
export const useDocgenStore = create<DocgenStore>((set, get) => ({
  data: null,
  setData: (data) => set({ data }),
  patchSection: (sectionId, next) =>
    set((state) => {
      if (!state.data) return state;
      const data = { ...state.data };
      for (const bucket of BUCKETS) {
        const idx = data[bucket].findIndex((s) => s.section_id === sectionId);
        if (idx !== -1) {
          const list = [...data[bucket]];
          list[idx] = { ...list[idx], ...next };
          data[bucket] = list;
          break;
        }
      }
      return { data };
    }),
  findSection: (sectionId) => {
    const data = get().data;
    if (!data) return null;
    for (const bucket of BUCKETS) {
      const index = data[bucket].findIndex((s) => s.section_id === sectionId);
      if (index !== -1) return { bucket, index, section: data[bucket][index] };
    }
    return null;
  },

  focusedSectionId: null,
  setFocusedSection: (id) => set({ focusedSectionId: id }),
  pendingAction: null,
  requestAction: (action) => set({ pendingAction: action }),
  clearPendingAction: (sectionId) =>
    set((state) =>
      state.pendingAction?.sectionId === sectionId
        ? { pendingAction: null }
        : state,
    ),
}));
